import { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { useIsMobile, reveal } from "@/lib/motion";
import { glassReveal } from "@/lib/glass";
import { LightfieldSchematic } from "@/components/home/LightfieldSchematic";
import { InvitationForm } from "@/components/home/InvitationForm";
import SomaticMirror from "@/components/home/SomaticMirror";

const AnamorphicRecess = lazy(() => import("@/components/recess/AnamorphicRecess"));

const BODY = [
  "For centuries, light has dictated its own terms—scattering aimlessly against ordinary screens or slipping like a ghost through clear glass. We have finally found a way to gently ask light to stay.",
  "Imagine the light from a projector as a stream of perfectly aimed rays. Our coating acts as an invisible, intelligent architecture. It catches those rays without altering their pure color or dimming their native brilliance. Instead, it holds them, guiding them forward and sweeping them softly outward to meet the human eye exactly where they need to be seen.",
  "Because we can shape the path of these rays with such quiet precision, a single sheet of glass can now hold multiple realities at once. You can weave different angles of light together so that true, floating depth emerges—no enclosures, no special glasses required. As someone walks past, the image shifts seamlessly with their perspective, honoring the profound and natural way our eyes perceive the world.",
  "We have accomplished this without asking the light to compromise. There is no loss of intensity, no shifting of its truth. It is a highly efficient optical transfer, yet it remains humble in its cost—accessible, affordable, and ready to be shaped.",
  "The canvas is awake. I invite you to show us what it can hold.",
];

// The close of Room I: the founder's call, set in the house grammar, with the one form that answers it.
export default function Invitation() {
  const mobile = useIsMobile();
  const fade = glassReveal(reveal(mobile, { duration: 1.0 }));
  return (
    <section id="invitation" aria-labelledby="invitation-heading" data-testid="invitation-section" className="relative bg-[#030305] room-pad">
      <div className="mx-auto max-w-6xl px-6">
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} {...fade} className="type-eyebrow">The invitation</motion.p>
        <motion.h2 id="invitation-heading" data-testid="invitation-heading" initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} {...fade} className="type-headline mt-4 max-w-3xl">
          I am looking for you.
        </motion.h2>
        <motion.p data-testid="invitation-subheading" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} {...fade} className="type-voice mt-8 max-w-2xl">
          I am looking for those who are most advanced in the creation of beauty and illusion.
        </motion.p>

        <div className="mt-16 grid gap-14 lg:grid-cols-[minmax(0,34rem)_minmax(0,1fr)] lg:gap-20">
          <div data-testid="invitation-body" className="space-y-6">
            {BODY.map((text, i) => (
              <motion.p key={i} data-testid={`invitation-paragraph-${i + 1}`} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} {...fade} className={`type-prose max-w-[66ch] ${i === BODY.length - 1 ? "text-[#F5F5F7]" : ""}`}>
                {text}
              </motion.p>
            ))}
          </div>

          <div className="flex flex-col gap-12 lg:pt-2">
            <motion.div data-testid="invitation-schematic" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} {...fade}>
              <LightfieldSchematic />
              <p data-testid="invitation-schematic-caption" className="type-record mt-3 text-white/45">
                Two images · one sheet · no glasses
              </p>
            </motion.div>
          </div>
        </div>

        <Suspense fallback={<div className="mt-20 aspect-[16/9] w-full" aria-busy="true" />}>
          <AnamorphicRecess />
        </Suspense>

        <SomaticMirror />

        <motion.div data-testid="invitation-bridge" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} {...fade} className="mx-auto mt-20 max-w-[1200px] border-t border-[#2A2A30]/80 pt-8">
          <p className="type-record mb-5 text-[#8E8E93]/70">Answer the invitation</p>
          <div className="max-w-xl"><InvitationForm /></div>
        </motion.div>
      </div>
    </section>
  );
}
